import * as React from "react";
import { Input, Modal, notification, Select } from "antd";
import { linksOf, request } from "../../../services/cpln";
import {
  RestoreVolumeReducerState,
  RunRestoreVolumeReducerActionType,
  useRunRestoreVolumeReducer,
} from "./reducer_restoreVolume";
import { VolumeSet } from "../../../schema/types/volumeSet/entity";

interface Props {
  volumeSet: VolumeSet;
  initialLocation?: string;
  initialVolumeIndex?: string;
  initialSnapshotName?: string;
  initialZone?: string;
  onClose: any;
  onDone: any;
  visible: boolean;
}

// Extract snapshot names for a specific volume in a specific location
export function getSnapshotNames(volumeSet: VolumeSet, locationName: string, volumeIndex: number): string[] {
  const snapshotNames: string[] = [];
  const location = (volumeSet.status?.locations || []).find(({ name: l }) => l === locationName);

  if (location) {
    if ((location.volumes || [])[volumeIndex]) {
      const volume = (location.volumes || [])[volumeIndex];
      (volume.volumeSnapshots || []).forEach((snapshot) => {
        snapshotNames.push(snapshot.name);
      });
    }
  }

  return snapshotNames;
}

export const CommandModal_RestoreVolume: React.FC<Props> = ({
  volumeSet,
  initialLocation,
  initialVolumeIndex,
  initialSnapshotName,
  initialZone,
  onClose,
  onDone,
  visible,
}) => {
  const selfLink = linksOf(volumeSet).self!;
  const [isLoading, setIsLoading] = React.useState(false);

  const [state, dispatch] = useRunRestoreVolumeReducer(initialLocation, initialVolumeIndex, initialSnapshotName, initialZone);

  async function handleConfirm() {
    try {
      setIsLoading(true);

      const spec: RestoreVolumeReducerState = JSON.parse(JSON.stringify(state));

      await request({
        method: "post",
        url: `${selfLink}/-command`,
        body: {
          type: "restoreVolume",
          spec: spec,
        },
      });
      onDone();
      notification.success({
        message: "Success",
        description: "Created Command.",
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  let isValid = true;
  if (!state.location) isValid = false;
  if (!state.volumeIndex) isValid = false;
  if (!state.snapshotName) isValid = false;

  return (
    <Modal
      width={450}
      open={visible}
      title="Restore Volume To Snapshot"
      okText={`Restore`}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: isLoading || !isValid, loading: isLoading, className: "action" }}
      onOk={handleConfirm}
      onCancel={onClose}
      maskClosable={false}
      destroyOnHidden
    >
      <div>Location</div>
      <Select
        className="mb-4 w-full"
        value={state.location}
        onChange={(value) => dispatch({ type: RunRestoreVolumeReducerActionType.LOCATION, payload: value })}
        options={(volumeSet.status?.locations || []).map(({ name: l }) => ({
          label: l,
          value: l,
          disabled: false,
          isLabel: false,
        }))}
      />
      <div>Volume Index</div>
      <Input
        name={"volumeIndex"}
        className="mb-4"
        value={state.volumeIndex}
        onChange={(e) => dispatch({ type: RunRestoreVolumeReducerActionType.INDEX, payload: e.target.value })}
      />
      <div>Snapshot Name</div>
      <Select
        className="mb-4 w-full"
        value={state.snapshotName}
        onChange={(value) => dispatch({ type: RunRestoreVolumeReducerActionType.NAME, payload: value })}
        options={getSnapshotNames(volumeSet, state.location, state.volumeIndex).map((name) => ({
          label: name,
          value: name,
          disabled: false,
          isLabel: false,
        }))}
      />
      <div>Zone (Optional)</div>
      <Input
        name={"zone"}
        className="mb-4"
        value={state.zone || ""}
        onChange={(e) => dispatch({ type: RunRestoreVolumeReducerActionType.ZONE, payload: e.target.value })}
      />
    </Modal>
  );
};
